/**
 * Notifications API
 *
 * In-app notification system. Each user's notifications are stored as:
 *   KV key: notifications:{username}  ->  NotificationItem[]
 *
 * Notification types:
 *   - comment:        Someone commented on your template
 *   - deadline:       A deadline is approaching (within 3 days) or overdue
 *   - status_change:  Template status was changed
 *   - assignment:     You were assigned to a template
 *   - mention:        Someone mentioned you in a comment
 *   - system:         System-generated notification
 */

import { apiFetch } from "./apiClient";

/* ================================================================
   Types
   ================================================================ */

export type NotificationType =
  | "comment"
  | "deadline"
  | "status_change"
  | "assignment"
  | "mention"
  | "system";

export interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  templateId?: string;
  templateName?: string;
  pageId?: string;
  /** Hash route to navigate to */
  link?: string;
  read: boolean;
  fromUser?: string;
  createdAt: number;
}

/* ================================================================
   Visual config
   ================================================================ */

export const NOTIFICATION_TYPE_CONFIG: Record<
  NotificationType,
  { label: string; icon: string; color: string }
> = {
  comment:       { label: "\u05EA\u05D2\u05D5\u05D1\u05D4",        icon: "\uD83D\uDCAC", color: "bg-blue-100 text-blue-700" },
  deadline:      { label: "\u05D3\u05D3\u05DC\u05D9\u05D9\u05DF",       icon: "\u23F0",  color: "bg-red-100 text-red-700" },
  status_change: { label: "\u05E9\u05D9\u05E0\u05D5\u05D9 \u05E1\u05D8\u05D8\u05D5\u05E1", icon: "\uD83D\uDD04", color: "bg-amber-100 text-amber-700" },
  assignment:    { label: "\u05D4\u05E7\u05E6\u05D0\u05D4",        icon: "\uD83D\uDC64", color: "bg-green-100 text-green-700" },
  mention:       { label: "\u05D0\u05D6\u05DB\u05D5\u05E8",         icon: "@",  color: "bg-purple-100 text-purple-700" },
  system:        { label: "\u05DE\u05E2\u05E8\u05DB\u05EA",        icon: "\u2139\uFE0F", color: "bg-gray-100 text-gray-700" },
};

/* ================================================================
   Helpers
   ================================================================ */

/** Returns true for network / abort / transient errors that should be silenced */
function isTransientError(err: unknown): boolean {
  if (err instanceof TypeError && /failed to fetch|network/i.test(err.message)) return true;
  if (err instanceof DOMException && err.name === "AbortError") return true;
  if (err instanceof Error && /abort|failed to fetch|network/i.test(err.message)) return true;
  return false;
}

/* ================================================================
   API functions
   ================================================================ */

/** Fetch all notifications for a user */
export async function getNotifications(username: string): Promise<NotificationItem[]> {
  try {
    const data = await apiFetch(`/notifications?username=${encodeURIComponent(username)}`);
    return Array.isArray(data.notifications) ? data.notifications : [];
  } catch (err) {
    // Suppress transient network errors (polling during deploy / navigation)
    if (!isTransientError(err)) {
      console.error("[notificationsApi] Failed to fetch notifications:", err);
    }
    return [];
  }
}

/** Send a notification to a specific user */
export async function sendNotification(
  targetUsername: string,
  notification: Omit<NotificationItem, "id" | "read" | "createdAt">,
): Promise<void> {
  try {
    await apiFetch("/notifications", {
      method: "POST",
      body: JSON.stringify({
        targetUsername,
        notification: {
          ...notification,
          id: Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 6),
          read: false,
          createdAt: Date.now(),
        },
      }),
    });
  } catch (err) {
    if (!isTransientError(err)) {
      console.error("[notificationsApi] Failed to send notification:", err);
    }
  }
}

/** Mark specific notifications as read */
export async function markAsRead(username: string, notificationIds: string[]): Promise<void> {
  try {
    await apiFetch("/notifications/read", {
      method: "PUT",
      body: JSON.stringify({ username, notificationIds }),
    });
  } catch (err) {
    if (!isTransientError(err)) {
      console.error("[notificationsApi] Failed to mark as read:", err);
    }
  }
}

/** Mark ALL notifications as read */
export async function markAllAsRead(username: string): Promise<void> {
  try {
    await apiFetch("/notifications/read-all", {
      method: "PUT",
      body: JSON.stringify({ username }),
    });
  } catch (err) {
    if (!isTransientError(err)) {
      console.error("[notificationsApi] Failed to mark all as read:", err);
    }
  }
}

/** Delete a single notification */
export async function deleteNotification(username: string, notificationId: string): Promise<void> {
  try {
    await apiFetch(`/notifications/${encodeURIComponent(notificationId)}?username=${encodeURIComponent(username)}`, {
      method: "DELETE",
    });
  } catch (err) {
    if (!isTransientError(err)) {
      console.error("[notificationsApi] Failed to delete notification:", err);
    }
  }
}

/** Clear all notifications for a user */
export async function clearAllNotifications(username: string): Promise<void> {
  try {
    await apiFetch(`/notifications/clear?username=${encodeURIComponent(username)}`, {
      method: "DELETE",
    });
  } catch (err) {
    if (!isTransientError(err)) {
      console.error("[notificationsApi] Failed to clear notifications:", err);
    }
  }
}

/* ================================================================
   Helper: generate a notification ID
   ================================================================ */

export function generateNotificationId(): string {
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 6);
}

/* ================================================================
   Email: send a standalone notification email via server
   ================================================================ */

/** Send an email notification to a user (requires user to have email configured) */
export async function sendNotificationEmailToUser(
  targetUsername: string,
  notification: Omit<NotificationItem, "id" | "read" | "createdAt">,
): Promise<{ sent: boolean; email?: string }> {
  try {
    const data = await apiFetch("/send-notification-email", {
      method: "POST",
      body: JSON.stringify({ targetUsername, notification }),
    });
    return { sent: data.sent ?? false, email: data.email };
  } catch (err) {
    if (!isTransientError(err)) {
      console.error("[notificationsApi] Failed to send notification email:", err);
    }
    return { sent: false };
  }
}
